import { Content } from "@/services/content/types";
import classNames from "classnames";

// EU:s ikoner för märkning av AI-genererat innehåll:
// https://digital-strategy.ec.europa.eu/en/policies/eu-icons-labelling-ai-generated-content
// Grundikonen används tillsammans med en svensk textetikett.

type AiLabelValue = "Helt AI-genererad" | "Delvis AI-modifierad";

const labels: Record<AiLabelValue, string> = {
  "Helt AI-genererad": "AI-genererad bild",
  "Delvis AI-modifierad": "AI-modifierad bild",
};

export const getAiLabelText = (image?: Content.Asset | null) => {
  const value = image?.fields?.aiLabel;
  return value && value in labels ? labels[value as AiLabelValue] : null;
};

type AiLabelProps = {
  image?: Content.Asset | null;
  // "sm" visar bara ikonen (texten finns kvar för skärmläsare), för små
  // tumnaglar där etiketten annars skulle täcka bilden.
  size?: "sm" | "md";
  className?: string;
};

const AiLabel = ({ image, size = "md", className }: AiLabelProps) => {
  const text = getAiLabelText(image);

  if (!text) return null;

  // Ligger över hela bilden så att den fungerar både i aspect-ratio-behållare
  // (där alla barn blir absolute inset-0) och i vanliga relative-behållare.
  return (
    <span
      className={classNames(
        "absolute inset-0 z-[1] pointer-events-none",
        className,
      )}
    >
      <span
        className={classNames(
          "absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-[#1d1d1b]/75 text-static-white font-semibold leading-none",
          size === "sm" ? "p-0.5" : "py-1 pl-1 pr-2.5 text-xs md:text-sm",
        )}
        title={size === "sm" ? text : undefined}
      >
        <AiIcon className={size === "sm" ? "h-5 w-5" : "h-5 w-5 md:h-6 md:w-6"} />
        <span className={size === "sm" ? "sr-only" : undefined}>{text}</span>
      </span>
    </span>
  );
};

const AiIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={classNames("shrink-0", className)}
    viewBox="89.28 100.72 365.49 365.49"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#fff"
      fillRule="evenodd"
      d="M272.03,100.72c100.92,0,182.74,81.82,182.74,182.75s-81.82,182.74-182.74,182.74-182.75-81.82-182.75-182.74,81.82-182.75,182.75-182.75"
    />
    <path
      fill="#1d1d1b"
      d="M170.79,353.74c-1.08,0-2.05-.43-2.92-1.31-.88-.87-1.31-1.84-1.31-2.92,0-.67.07-1.27.2-1.81l47.34-129.32c.4-1.48,1.24-2.79,2.52-3.93,1.27-1.14,3.05-1.71,5.34-1.71h29.81c2.28,0,4.06.57,5.34,1.71,1.27,1.14,2.11,2.45,2.52,3.93l47.14,129.32c.27.54.4,1.14.4,1.81,0,1.08-.44,2.05-1.31,2.92s-1.91,1.31-3.12,1.31h-24.78c-2.01,0-3.52-.5-4.53-1.51-1.01-1.01-1.65-1.91-1.91-2.72l-7.86-20.55h-53.78l-7.65,20.55c-.27.81-.88,1.71-1.81,2.72-.94,1.01-2.55,1.51-4.83,1.51h-24.78ZM218.13,299.96h37.47l-18.93-53.18-18.53,53.18Z"
    />
    <path
      fill="#1d1d1b"
      d="M328.11,353.74c-1.48,0-2.69-.47-3.63-1.41-.94-.94-1.41-2.15-1.41-3.63v-130.93c0-1.48.47-2.68,1.41-3.63s2.15-1.41,3.63-1.41h26.99c1.48,0,2.68.47,3.63,1.41.94.94,1.41,2.15,1.41,3.63v130.93c0,1.48-.47,2.69-1.41,3.63-.94.94-2.15,1.41-3.63,1.41h-26.99Z"
    />
  </svg>
);

export default AiLabel;
